import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  X, Tv, Monitor, Smartphone, Globe, MapPin, Users, Clock,
  Loader2, Pencil, Play, Film, FileText, Image as ImageIcon,
  Music, Link2, Layers, Hand, ArrowLeftRight, AlertTriangle,
} from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import {
  getScreen, getScreenLogs, getScreenContent, getScreenGroups, getScreenChannels,
  type ScreenContentSync, type ScreenChannels,
} from '@meno/ui/lib/api'
import { formatRelativeSyncTime } from '@meno/ui/lib/utils'
import { getOverlayContainer } from '@meno/ui/lib/overlayContainer'
import { useScreenStatus } from '@meno/ui/contexts/ScreenStatusContext'
import ScreenActivityLog, { type ScreenLog } from '@/components/ScreenDetails/ScreenActivityLog'
import { pickCurrentSyncItem, SyncItem, SyncLayoutGrid, SyncPreview, TRANSITIONS } from '@/components/ScreenDetails/PreviewColumn'
import type { RotationDegrees } from '@/components/ScreenDetails/editBufferReducer'
import { InfoCard, InfoRow, SectionLabel, DeviceInfoSection, ConnectionSection } from '@/components/ScreenDetails/DeviceTelemetryPanel'
import { DeviceCommandsPanel } from '@/components/ScreenDetails/DeviceCommandsPanel'
import { screenDisplayStatus, ScreenStatusBadge } from '@meno/ui/components/shared/ScreenStatusBadge'

// Fuller screen shape than the list page carries — populated by getScreen on
// open. Every field is optional so a partial payload never crashes a tab.
type FullScreen = {
  id: number; public_id: string; name: string; status: string; branch_id?: number
  location?: string; ip_address?: string; mac_address?: string
  orientation_type?: 'horizontal' | 'vertical'; rotation_degrees?: number
  resolution?: string; is_active?: boolean; created_at?: string; last_seen?: string
  device_kind?: 'android' | 'web'; device_model?: string; os_version?: string
  app_version?: string; screenshot_url?: string; screenshot_taken_at?: string | null; is_screen_live?: boolean
  // Numeric build + release track (Android only — web has no update channel
  // concept). Kiosk-health flags: can the player relaunch itself after a
  // reboot, and is it actually set as the device's home/launcher app.
  app_version_code?: number | null; app_channel?: string | null
  auto_launch_permission?: boolean | null; auto_launch_enabled?: boolean | null; is_home_app?: boolean | null
  // License expiry (mirror of the active license's expires_at). NULL = perpetual.
  disconnect_at?: string | null
  // Connection/device telemetry (heartbeat-reported; see Screen model).
  // device_boot_at is Android-only — web players show N/A for uptime/boot.
  timezone?: string | null; device_boot_at?: string | null
  latency_ms?: number | null; last_synced_at?: string | null
  battery_pct?: number | null; battery_charging?: boolean | null
  latency_history?: { t: string; ms: number }[] | null
  latitude?: number | null; longitude?: number | null
  menus?: { id: number; name: string }[]; media?: { id: number }[]; playlists?: { id: number }[]
  display_settings?: { transition?: string; transition_speed?: string } | null
}

type TabKey = 'overview' | 'device' | 'connection' | 'content' | 'activity' | 'controls'

// A right-anchored slide-in panel that surfaces everything about a single
// screen without leaving the list. Data is loaded lazily on open (screen,
// logs, current content, group membership). Metrics the backend does not
// report (CPU / temperature / signal / health score) are intentionally left
// out rather than faked — the tabs show only what the device actually sends.
export default function ScreenInfoDrawer({
  open, publicId, onClose, onDeleted,
}: {
  open: boolean
  publicId: string | null
  onClose: () => void
  onDeleted?: () => void
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [tab, setTab] = useState<TabKey>('overview')
  const [screen, setScreen] = useState<FullScreen | null>(null)
  const [logs, setLogs] = useState<ScreenLog[]>([])
  const [sync, setSync] = useState<ScreenContentSync | null>(null)
  const [channels, setChannels] = useState<ScreenChannels | null>(null)
  const [groupName, setGroupName] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  // Scrollable body — reset to the top on every open so a reopened drawer
  // never lands mid-scroll on a stale position.
  const bodyRef = useRef<HTMLDivElement | null>(null)

  const load = async (pid: string) => {
    setLoading(true)
    try {
      const [scr, logsRes, groups] = await Promise.all([
        getScreen(pid),
        getScreenLogs(pid).catch(() => ({ data: [] })),
        getScreenGroups().catch(() => ({ data: [] })),
      ])
      setScreen(scr.data)
      setLogs(logsRes.data ?? [])
      // Content/Audio/Stop channels — same source ScreenDetailsPage's channel
      // cards use, so the drawer's Content tab shows the exact same picture
      // of what the player actually renders (content + audio + stop layer),
      // each channel declared separately rather than folded into one blob.
      getScreenChannels(pid).then(r => setChannels(r.data)).catch(() => setChannels(null))
      const inGroups = (groups.data || [])
        .filter((g: { screens?: { id: number }[] }) => g.screens?.some(s => s.id === scr.data.id))
        .map((g: { name: string }) => g.name)
      setGroupName(inGroups.length ? inGroups.join(', ') : null)
      // Current content is best-effort — a never-paired screen 404s here.
      getScreenContent(pid).then(r => setSync(r.data)).catch(() => setSync(null))
    } catch {
      toast.error(t('screens.infoLoadFailed', 'Could not load screen details.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open && publicId) {
      // Full reset on every open — tab, data and scroll all go back to their
      // initial state before the fresh payload loads.
      setTab('overview'); setScreen(null); setLogs([]); setSync(null); setChannels(null); setGroupName(null)
      bodyRef.current?.scrollTo({ top: 0 })
      load(publicId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, publicId])

  // Live telemetry — heartbeats land every ~30s, so re-pull the screen row on
  // the same cadence while the drawer is open (silently, no loading state).
  // Keeps battery / latency / the connection graph current instead of
  // freezing at whatever the drawer loaded with. status/last_seen additionally
  // get an instant push overlay below — the poll only carries telemetry the
  // WS event doesn't (see screen_status's payload in screen_status_broadcast.py).
  useEffect(() => {
    if (!open || !publicId) return
    const id = window.setInterval(() => {
      getScreen(publicId)
        .then(r => setScreen(prev => (prev ? { ...prev, ...r.data } : r.data)))
        .catch(() => { /* transient refresh miss — next tick retries */ })
    }, 30_000)
    return () => window.clearInterval(id)
  }, [open, publicId])

  // Instant status overlay — reflects a screen_status push the moment it
  // arrives instead of waiting for the next 30s poll tick above.
  const liveStatus = useScreenStatus()
  useEffect(() => {
    if (!screen) return
    const entry = liveStatus(screen.id)
    if (!entry) return
    setScreen(prev => (prev && (prev.status !== entry.status || prev.last_seen !== entry.last_seen))
      ? { ...prev, status: entry.status, last_seen: entry.last_seen ?? prev.last_seen }
      : prev)
  }, [screen?.id, liveStatus])

  // Ping + resync/restart/screenshot device commands now live in
  // DeviceCommandsPanel (rendered on the Controls tab below) — the same
  // component ScreenDetailsPage's Device tab uses, instead of a second
  // parallel implementation here.

  const isWeb = (screen?.device_kind ?? 'android') === 'web'
  // Single source of truth for "expired" (screenDisplayStatus, shared with
  // the screens list / dashboard / admin) — surfaced on the screenshot
  // button, the channel summary, and the preview frame in the Content tab.
  const expired = !!screen && screenDisplayStatus(screen) === 'expired'
  const items = sync?.content.items ?? []
  const currentItem = items[0]
  const nextItem = items[1]

  // Computed-preview inputs: anchor the rotation clock at last_seen (falling
  // back to server_now) exactly like the screen page's Sync preview does.
  const previewAnchor = sync ? new Date(sync.last_seen ?? sync.server_now).getTime() : 0
  const previewPick = sync ? pickCurrentSyncItem(items, previewAnchor, Date.now()) : null
  const hasComputedPreview = !!sync && ((sync.content.layout?.zones.length ?? 0) > 0 || items.length > 0)

  const TABS: { key: TabKey; label: string }[] = [
    { key: 'overview',   label: t('screens.tab.overview', 'Overview') },
    { key: 'device',     label: t('screens.tab.deviceInfo', 'Device Info') },
    { key: 'connection', label: t('screens.tab.connection', 'Connection') },
    { key: 'content',    label: t('screens.tab.content', 'Content') },
    { key: 'activity',   label: t('screens.tab.activity', 'Activity Log') },
    { key: 'controls',   label: t('screens.tab.controls', 'Controls') },
  ]

  const isRTL = i18n.language === 'ar'

  return createPortal(
    <>
      {/* Backdrop — darkens the WHOLE page (sidebar + top bar included) so the
          drawer reads as the only active surface. */}
      <div
        className={`fixed inset-0 z-40 bg-black/60 transition-opacity duration-200 ${open ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={onClose}
        aria-hidden
      />
      {/* Panel — full viewport height, pinned to the inline-end edge; slides in
          on open. Corner rounding comes from the --radius token (rounded-lg). */}
      <aside
        dir={isRTL ? 'rtl' : 'ltr'}
        className={`fixed inset-y-0 end-0 z-50 w-full max-w-[640px] bg-background border-s border-border shadow-2xl rounded-s-lg overflow-hidden flex flex-col transition-transform duration-200 ${open ? 'translate-x-0' : 'ltr:translate-x-full rtl:-translate-x-full'}`}
        role="dialog"
        aria-label={t('screens.screenDetails', 'Screen details')}
        // Closed, this panel is only slid off-screen — it stays mounted so the
        // transition can play. Without inert it also stays TABBABLE: measured
        // 7 reachable controls (Close, and every tab) sitting outside the
        // viewport, so a keyboard user working down the screens list watches
        // the focus ring vanish for seven stops. Assistive tech likewise saw a
        // dialog labelled "Screen details" permanently present, announcing
        // "Screen not found" from the null-publicId render.
        //
        // inert removes the subtree from both the tab order and the
        // accessibility tree in one attribute; aria-hidden is kept alongside
        // it for anything that does not implement inert. They agree here
        // precisely because inert also drops focusability — aria-hidden on
        // still-focusable content would be its own violation.
        inert={!open}
        aria-hidden={!open || undefined}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border shrink-0">
          <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
            <Tv className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-base font-semibold truncate">{screen?.name ?? t('common.loading', 'Loading…')}</h2>
              {screen && <ScreenStatusBadge screen={screen} />}
            </div>
            <p className="text-[11px] text-muted-foreground">{t('screens.screenDetails', 'Screen details')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="w-8 h-8 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab strip — horizontally scrollable so all six fit on a narrow panel. */}
        <div className="border-b border-border shrink-0 overflow-x-auto">
          <div className="flex px-2 min-w-max">
            {TABS.map(tb => (
              <button
                key={tb.key}
                type="button"
                onClick={() => setTab(tb.key)}
                className={`px-3 py-2.5 text-[13px] font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                  tab === tb.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tb.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div ref={bodyRef} className="flex-1 min-h-0 overflow-y-auto p-5">
          {loading && !screen ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : !screen ? (
            <p className="text-sm text-muted-foreground text-center py-16">{t('screens.notFound', 'Screen not found.')}</p>
          ) : (
            <>
              {tab === 'overview' && (
                <div className="space-y-4">
                  <InfoCard>
                    <InfoRow label={t('screens.screenName', 'Screen name')} value={screen.name} />
                    <InfoRow
                      label={t('screens.screenType', 'Screen type')}
                      value={isWeb ? t('screens.webPlayer', 'Web Browser') : t('screens.androidPlayer', 'Android Player')}
                      icon={isWeb ? Globe : Smartphone}
                    />
                    <InfoRow
                      label={t('common.status', 'Status')}
                      value={<span className="capitalize">{t(`common.${screenDisplayStatus(screen)}`, screenDisplayStatus(screen))}</span>}
                    />
                    {screen.location && <InfoRow label={t('screens.location', 'Location')} value={screen.location} icon={MapPin} />}
                    <InfoRow label={t('screens.group', 'Group')} value={groupName ?? '—'} icon={Users} />
                    <InfoRow label={t('screens.lastSeen', 'Last seen')} value={formatRelativeSyncTime(screen.last_seen ?? screen.created_at ?? '')} icon={Clock} />
                  </InfoCard>

                  {/* Screen preview — a device screenshot when one was ever
                      uploaded; otherwise a computed render of what the screen
                      should be showing right now (same math as the Sync
                      preview on the screen page). Players don't upload
                      screenshots yet, so the computed render is the usual
                      path. */}
                  <div>
                    {/* Always "Screenshot" now (was "Screen preview" when no
                        real shot existed yet) — a real one gets the screen's
                        name in the heading too; either way the caption below
                        still says plainly when it's a computed approximation
                        rather than an actual captured image. */}
                    <SectionLabel>
                      {screen.screenshot_url
                        ? t('screens.screenshotOf', '{{name}} — Screenshot', { name: screen.name })
                        : t('screens.screenshotHeading', 'Screenshot')}
                    </SectionLabel>
                    <div className="relative rounded-xl border border-border bg-black/90 aspect-video overflow-hidden flex items-center justify-center">
                      {screen.screenshot_url ? (
                        <img src={screen.screenshot_url} alt={t('screens.screenshotOf', '{{name}} — Screenshot', { name: screen.name })} className="w-full h-full object-contain" />
                      ) : hasComputedPreview && sync ? (
                        sync.content.layout && sync.content.layout.zones.length > 0 ? (
                          <SyncLayoutGrid layout={sync.content.layout} startedAt={previewAnchor} now={Date.now()} t={t} />
                        ) : (
                          <SyncItem item={previewPick!.current} offsetSec={previewPick!.offsetSec} t={t} />
                        )
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-white/40">
                          <Monitor className="w-8 h-8" />
                          <span className="text-xs">{t('screens.noPreview', 'No preview available')}</span>
                        </div>
                      )}
                    </div>
                    {screen.screenshot_url && screen.screenshot_taken_at && (
                      <p className="text-[11px] text-muted-foreground mt-1.5">
                        {t('screens.screenshotTakenAt', 'Captured {{when}}', { when: formatRelativeSyncTime(screen.screenshot_taken_at) })}
                      </p>
                    )}
                    {!screen.screenshot_url && hasComputedPreview && (
                      <p className="text-[11px] text-muted-foreground mt-1.5">
                        {t('screens.previewComputed', 'Approximation computed from the screen’s current content.')}
                      </p>
                    )}
                  </div>

                  {/* Current content */}
                  <div>
                    <SectionLabel>{t('screens.currentContent', 'Current content')}</SectionLabel>
                    <InfoCard>
                      {currentItem ? (
                        <>
                          <div className="flex items-center gap-2.5 px-4 py-3">
                            <ContentIcon type={currentItem.type} />
                            <span className="text-sm font-medium truncate flex-1">{currentItem.name}</span>
                            {currentItem.duration ? (
                              <span className="text-xs text-muted-foreground tabular-nums">{fmtDur(currentItem.duration)}</span>
                            ) : null}
                          </div>
                          {nextItem && (
                            <>
                              <Divider />
                              <div className="flex items-center gap-2.5 px-4 py-3">
                                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{t('screens.nextContent', 'Next')}</span>
                                <span className="text-sm truncate flex-1 text-muted-foreground">{nextItem.name}</span>
                              </div>
                            </>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground px-4 py-4">{t('screens.noContentAssigned', 'No content assigned yet.')}</p>
                      )}
                    </InfoCard>
                  </div>
                </div>
              )}

              {tab === 'device' && <DeviceInfoSection screen={screen} />}

              {tab === 'connection' && <ConnectionSection screen={screen} logs={logs} />}

              {tab === 'content' && (
                <div className="space-y-3">
                  {/* The screen's actual live preview panel — the exact same
                      SyncPreview component ScreenDetailsPage's Sync mode
                      renders (apps/frame/src/components/ScreenDetails/
                      PreviewColumn.tsx), not a reimplementation. It polls the
                      screen's content itself, independent of the `sync` state
                      this drawer loads once on open (used below for the
                      itemized list + layout badge). */}
                  <SyncPreview
                    screenId={publicId!}
                    orientationType={screen.orientation_type === 'vertical' ? 'vertical' : 'horizontal'}
                    rotationDegrees={([90, 180, 270].includes(screen.rotation_degrees ?? 0) ? screen.rotation_degrees : 0) as RotationDegrees}
                    t={t}
                    expired={expired}
                  />

                  {/* Every channel a player actually renders, each declared on
                      its own row instead of folded into one summary — mirrors
                      the real device manifest (ContentResponse carries
                      transition/stop_screen alongside the content items, see
                      backend/app/services/content.py) rather than only ever
                      showing the plain content items below. Content/Audio/Stop
                      come from the same /screens/{id}/channels endpoint
                      ScreenDetailsPage's channel cards use. */}
                  {expired && (
                    <div className="flex items-center gap-2.5 rounded-xl border border-destructive bg-destructive px-4 py-2.5 text-white text-sm font-semibold">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      {t('screens.expiredTitle', 'License expired')}
                    </div>
                  )}
                  <InfoCard>
                    <InfoRow
                      label={t('screens.screenContent', 'Screen Content')}
                      icon={Film}
                      value={channels?.content.find(c => c.active)?.content_name ?? '—'}
                    />
                    <InfoRow
                      label={t('screens.audioTrack', 'Audio Track')}
                      icon={Music}
                      value={channels?.audio.find(c => c.active)?.content_name ?? '—'}
                    />
                    <InfoRow
                      label={t('screens.stopScreen', 'Stop Screen')}
                      icon={Hand}
                      value={channels?.stop.find(c => c.active)?.content_name ?? '—'}
                    />
                    <InfoRow
                      label={t('screens.transitions', 'Transitions')}
                      icon={ArrowLeftRight}
                      value={(() => {
                        const key = screen.display_settings?.transition || 'default'
                        const meta = TRANSITIONS.find(o => o.key === key) ?? TRANSITIONS[0]
                        return t(meta.labelKey, meta.fallback)
                      })()}
                    />
                  </InfoCard>

                  <SectionLabel>{t('screens.nowPlaying', 'Now playing')} · {items.length}</SectionLabel>
                  {items.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-8 text-center">{t('screens.noContentAssigned', 'No content assigned yet.')}</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {items.map((it, i) => (
                        <li key={`${it.type}-${it.id}-${i}`} className="flex items-center gap-2.5 rounded-lg border border-border/60 bg-card px-3 py-2.5">
                          <ContentIcon type={it.type} />
                          <span className="text-sm font-medium truncate flex-1">{it.name}</span>
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{it.type}</span>
                          {it.duration ? <span className="text-xs text-muted-foreground tabular-nums">{fmtDur(it.duration)}</span> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                  {sync?.content.layout && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Layers className="w-3.5 h-3.5" /> {t('screens.layoutActive', 'Zoned layout active')}: {sync.content.layout.name}
                    </div>
                  )}
                </div>
              )}

              {tab === 'activity' && <ScreenActivityLog logs={logs} compact screenName={screen?.name} />}

              {tab === 'controls' && (
                <DeviceCommandsPanel
                  publicId={publicId!}
                  isWeb={isWeb}
                  autoLaunchEnabled={screen?.auto_launch_enabled}
                  onScreenshotTaken={() => { if (publicId) load(publicId) }}
                  onRefresh={() => publicId && load(publicId)}
                  onDelete={onDeleted ? () => { onClose(); onDeleted() } : undefined}
                  expired={expired}
                />
              )}
            </>
          )}
        </div>

        {/* Footer — jump to the full editor. */}
        {screen && (
          <div className="border-t border-border p-3 shrink-0">
            <Button className="w-full gap-2" onClick={() => navigate(`/screens/${screen.public_id}`)}>
              <Pencil className="w-4 h-4" />
              {t('screens.openFullDetails', 'Open full screen page')}
            </Button>
          </div>
        )}
      </aside>
    </>,
    // Portal into the app shell (not document.body) — Radix overlays (the
    // activity log's filter Select, dropdowns) portal into `.app-shell`, so a
    // body-level drawer with the same z-index painted OVER its own dropdowns
    // (later DOM sibling wins at equal z). Sharing the container restores the
    // natural order: overlays mount after the drawer and stack above it. It
    // also puts the drawer inside the shell's theme scope, per the
    // overlayContainer rule.
    getOverlayContainer() ?? document.body,
  )
}

// ── Small building blocks ──────────────────────────────────────────────────

function Divider() { return <div className="border-t border-border" /> }

// InfoCard / InfoRow / SectionLabel / ControlButton now live in
// DeviceTelemetryPanel.tsx / DeviceCommandsPanel.tsx (imported above) —
// this file no longer keeps its own copies.

// Map a manifest item type onto the same icon family the content pickers use.
function ContentIcon({ type }: { type: string }) {
  const cls = 'w-4 h-4 text-muted-foreground shrink-0'
  if (type === 'menu')  return <Link2 className={cls} />
  if (type === 'media') return <Film className={cls} />
  if (type === 'audio') return <Music className={cls} />
  if (type === 'image') return <ImageIcon className={cls} />
  if (type === 'document' || type === 'pdf') return <FileText className={cls} />
  return <Play className={cls} />
}

function fmtDur(sec: number) {
  const m = Math.floor(sec / 60)
  const s = Math.round(sec % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

// batteryIcon / MeterBar / signalQuality / SignalBars /
// LatencyGraph / fmtUptime now live in DeviceTelemetryPanel.tsx.
